import { FunctionComponent } from 'react'

import dynamic from 'next/dynamic'
import styled, { css } from 'styled-components'

import config from '@config'
import {
  desktopCenteredContentCSS,
  desktopCSS,
  desktopLeftAlignedCSS,
  mobileAndTabletCSS,
} from '@measures/responsive'
import { printCSS } from '@utils/style'
import useEnableEnhancedSearchPage from '@hooks/useEnableEnhancedSearchPage'
import useGetPageIdentity from '@hooks/useGetPageIdentity'
import useIsContentForceCentered from '@hooks/useIsContentForceCentered'
import useIsCUIEnabled from '@hooks/useIsCUIEnabled'
import useIsImmersiveHeaderActive from '@hooks/useIsImmersiveHeaderActive'
import useIsPianoSDKLoaded from '@hooks/useIsPianoSDKLoaded'
import useIsThemeSwitcherEnabled from '@hooks/useIsThemeSwitcherEnabled'
import FeatureFlagIndicator from '@components/FeatureFlagIndicator'
import GeolocationIndicator from '@components/Header/FirstRow/GeolocationIndicator'
import UserProfilePageLink from '@components/Header/FirstRow/UserProfilePageLink'
import LanguageSwitcher from '@components/LanguageSwitcher'

import BurgerMenu from './BurgerMenu'
import EnhancedSearchLink from './EnhancedSearchLink'
import LeftButtonsContainer from './LeftButtonsContainer'
import Logo from './Logo'
import OfferPageLink from './OfferPageLink'
import SearchInput from './SearchInput'

const ThemeSwitcher = dynamic(() => import('@components/ThemeSwitcher'), {
  ssr: false,
})

const isStorybook = process.env.STORYBOOK

const {
  publication: { publication },
} = config

interface StyledFirstRowProps {
  isContentForceCentered: boolean
  isImmersiveHeaderActive: boolean
  isHerotelling: boolean
  isUserProfilePage: boolean
}

const StyledFirstRow = styled.div<StyledFirstRowProps>`
  ${({
    theme: {
      colors: { fillDefault },
    },
    isContentForceCentered,
    isImmersiveHeaderActive,
    isHerotelling,
    isUserProfilePage,
  }) => css`
    background-color: ${fillDefault};
    transition: background-color 0.5s linear;

    z-index: 2;
    position: relative;

    display: grid;
    grid-template-columns: 994px;
    align-items: center;
    height: 80px;

    ${isImmersiveHeaderActive &&
    css`
      ${mobileAndTabletCSS(css`
        background-color: transparent;
      `)}
    `}

    ${desktopCSS(css`
      justify-content: center;
      ${!(isHerotelling || isUserProfilePage) &&
      desktopLeftAlignedCSS(css`
        ${!isContentForceCentered &&
        css`
          justify-content: flex-start;
        `};
      `)};
    `)};

    ${desktopCenteredContentCSS(css`
      justify-content: center;
    `)}

    ${isContentForceCentered &&
    css`
      justify-content: center;
    `}

    ${mobileAndTabletCSS(css`
      grid-template-columns: 1fr;
      height: 48px;
    `)}
  `}
`

const MainContainer = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    padding: 0 ${spacing16};

    ${desktopCSS(css`
      padding: 0 15px;
    `)}
  `}
`

const StyledLanguageSwitcher = styled(LanguageSwitcher)`
  ${mobileAndTabletCSS(css`
    display: none;
  `)}
`

const StyledSearchInput = styled(SearchInput)`
  ${mobileAndTabletCSS(css`
    display: none;
  `)}
`

const RightButtonsContainer = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    justify-self: flex-end;
    display: grid;
    grid-auto-flow: column;
    align-items: center;

    ${printCSS(css`
      display: none;
    `)}

    ${desktopCSS(css`
      grid-gap: ${spacing16};
    `)}
  `}
`
const ChatbotEntry = dynamic(() => import('@components/Chatbot/ChatbotEntry'), {
  ssr: false,
})

const StyledPlaceholder = styled.div`
  width: 40px;
  height: 40px;

  ${mobileAndTabletCSS(css`
    width: 48px;
    height: 48px;
  `)};
`

const FirstRow: FunctionComponent<{
  forceCenter?: boolean
  removeLanguageSwitcher?: boolean
}> = ({ forceCenter, removeLanguageSwitcher }) => {
  const { isHerotelling, isUserProfilePage } = useGetPageIdentity()
  const isContentForceCentered = useIsContentForceCentered()
  const isImmersiveHeaderActive = useIsImmersiveHeaderActive()
  const isCUIEnabled = useIsCUIEnabled()
  const isPianoSDKLoaded = useIsPianoSDKLoaded()
  const isEnhancedSearch = useEnableEnhancedSearchPage()

  return (
    <StyledFirstRow
      isContentForceCentered={!!forceCenter || isContentForceCentered}
      isImmersiveHeaderActive={isImmersiveHeaderActive}
      isHerotelling={isHerotelling}
      isUserProfilePage={isUserProfilePage}
    >
      <GeolocationIndicator />
      <MainContainer>
        <LeftButtonsContainer>
          <BurgerMenu />
          {!isEnhancedSearch && <StyledSearchInput />}
          {publication !== 'romandie' && isPianoSDKLoaded && isCUIEnabled && (
            <StyledPlaceholder>
              <ChatbotEntry />
            </StyledPlaceholder>
          )}
          {isEnhancedSearch && <EnhancedSearchLink />}
          <FeatureFlagIndicator />
        </LeftButtonsContainer>
        {[!isStorybook, useIsThemeSwitcherEnabled()].every(Boolean) && (
          <ThemeSwitcher />
        )}
        <Logo />
        <RightButtonsContainer>
          {!removeLanguageSwitcher && <StyledLanguageSwitcher />}
          {publication !== 'romandie' && <OfferPageLink />}
          <UserProfilePageLink />
        </RightButtonsContainer>
      </MainContainer>
    </StyledFirstRow>
  )
}

export default FirstRow
